import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Search, Megaphone, Trophy, Users, Heart, Share2, MessageSquare, MapPin, Zap, ChevronLeft, ChevronRight, History, Bell, BadgeCheck, Mail, CalendarX, CalendarCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Config, UserProfile, Post, Player, FlashBanner, Match, Team, Tournament } from '../../types';
import { collection, onSnapshot, query, orderBy, limit, where, or } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { FilterChips } from './FilterChips';
import { PostCard } from './PostCard';
import { SocialPublicMatchBanner } from './SocialPublicMatchBanner';
import { PlayerCardPremium } from '../player/PlayerCardPremium';
import { PostCreator } from './PostCreator';
import { ALL_CITIES, getCityName } from '../../data/locations';
import { FadeInImage } from '../common/FadeInImage';
import { PairAvatar } from '../common/PairAvatar';
import { getMatchTemporalState } from '../../lib/matchmakingUtils';
import { getCategoryStyles } from '../../lib/categoryStyles';

interface SocialDashboardProps {
  userProfile?: UserProfile | null;
  currentPlayer?: Player;
  config: Config;
  sedesPublicas?: UserProfile[];
  onContactClick?: (playerId: string, playerName: string, playerPhoto?: string) => void;
  onPlayerClick?: (playerId: string | null) => void;
  onSedeClick?: (sedeId: string) => void;
  onSupportClick?: () => void;
  globalCity?: string;
  matches?: Match[];
  players?: Player[];
  teams?: Team[];
  tournaments?: Tournament[];
  recentNotifications?: any[];
  onMatchClick?: (match: Match) => void;
}

const isMatchPast = (date: string, time: string): boolean => {
  try {
    const [y, m, d] = date.split('-').map(Number);
    const [h, min] = time.split(':').map(Number);
    const matchDateTime = new Date(y, m - 1, d, h, min, 0, 0);
    return matchDateTime < new Date();
  } catch {
    return false;
  }
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};

export function SocialDashboard({

  userProfile,
  currentPlayer,
  config,
  sedesPublicas = [],
  onContactClick,
  onPlayerClick,
  onSedeClick,
  onSupportClick,
  globalCity = '',
  matches = [],
  players = [],
  teams = [],
  tournaments = [],
  recentNotifications = [],
  onMatchClick
}: SocialDashboardProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [flashBanners, setFlashBanners] = useState<FlashBanner[]>([]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [interactionTime, setInteractionTime] = useState(Date.now());
  const [cardRotation, setCardRotation] = useState(0);
  const [myBookings, setMyBookings] = useState<any[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);

  // Lazy Loading state
  const [postLimit, setPostLimit] = useState(10);
  const bottomBoundaryRef = useRef<HTMLDivElement>(null);

  // Pagination Observer
  useEffect(() => {
    // Fetch upcoming bookings for user
    const todayStr = new Date().toISOString().split('T')[0];
    if (!userProfile?.uid) {
      setMyBookings([]);
      return () => {};
    }

    const bookingsQ = query(
      collection(db, getCollectionPath('bookings')),
      where('playerId', '==', userProfile.uid)
    );
    const unsubBookings = onSnapshot(bookingsQ, (snap) => {
      const bks = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter((b: any) => {
        const isNotExcluded = !b.excludedDates || !b.excludedDates.includes(b.date);
        if (!isNotExcluded || !(b.status === 'confirmed' || b.status === 'paid' || b.status === 'pending') || b.date < todayStr) {
          return false;
        }
        if (b.date === todayStr) {
          const timeToCompare = b.startTime || b.timeSlot;
          if (timeToCompare) {
            const now = new Date();
            const currentHour = now.getHours().toString().padStart(2, '0');
            const currentMin = now.getMinutes().toString().padStart(2, '0');
            const currentTimeStr = `${currentHour}:${currentMin}`;
            if (timeToCompare < currentTimeStr) return false;
          }
        }
        return true;
      }).sort((a: any, b: any) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      setMyBookings(bks);
    });
    return () => unsubBookings();
  }, [userProfile?.uid]);

  // Pagination Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPostLimit((prev) => prev + 10);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = bottomBoundaryRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Subscribe to flash banners
  useEffect(() => {
    const q = query(collection(db, 'flash_banners'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedBanners = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FlashBanner[];
      setFlashBanners(fetchedBanners);
    }, (error) => {
      console.error("Error fetching flash banners:", error);
    });

    return () => unsubscribe();
  }, []);

  // Filter and sort active banners client-side (matching current city and either created by superadmin or under 2 hours old)
  const activeFlashBanners = flashBanners.filter(banner => {
    const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
    return (banner.city === globalCity || globalCity === 'Todas las ciudades' || banner.city === 'Todas las ciudades') && (banner.createdBySuperAdmin || banner.createdAt > twoHoursAgo);
  });
  activeFlashBanners.sort((a, b) => b.createdAt - a.createdAt);

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setInteractionTime(Date.now());
    setCurrentSlide(prev => (prev + newDirection + activeFlashBanners.length) % activeFlashBanners.length);
  };

  // Reset slide index when length of active banners changes
  useEffect(() => {
    setCurrentSlide(0);
    setDirection(0);
  }, [activeFlashBanners.length]);

  // Autoplay for flash banners
  useEffect(() => {
    if (activeFlashBanners.length <= 1) return;
    const interval = setInterval(() => {
      paginate(1);
    }, 4000);
    return () => clearInterval(interval);
  }, [activeFlashBanners.length, interactionTime]);

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'), limit(postLimit));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      setPosts(fetchedPosts);
    });

    return () => unsubscribe();
  }, [postLimit]);

  const primary = config.primaryColor || '#eab308';
  const catStyles = getCategoryStyles((currentPlayer || userProfile as unknown as Player)?.category || '');

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedGender, setSelectedGender] = useState<string | null>(null);

  const categories = ['1ra', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va'];
  const genders = ['Masculino', 'Femenino', 'Mixto'];

  // Logic to filter posts based on chips
  const filteredPosts = posts.filter(post => {
    // 1. City Filtering
    if (post.city && post.city !== globalCity && post.city !== 'Todas las ciudades' && globalCity !== 'Todas las ciudades') return false;

    // 3. Category & Gender Chips
    if (selectedCategories.length > 0 && (!post.targetCategories || !post.targetCategories.some(c => selectedCategories.includes(c)))) {
      return false;
    }
    if (selectedGender && post.targetGender !== selectedGender) {
      return false;
    }

    return true;
  });

  // Sort logic for Full Plus priority
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const isAFullPlus = a.isPriority || (a.authorRole === 'admin' && sedesPublicas.find(s => s.uid === a.authorId)?.subscriptionPlan === 'Full Plus');
    const isBFullPlus = b.isPriority || (b.authorRole === 'admin' && sedesPublicas.find(s => s.uid === b.authorId)?.subscriptionPlan === 'Full Plus');

    // Give priority to Full Plus posts if they are from the last 7 days (604800000 ms)
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const aIsRecentFullPlus = isAFullPlus && (now - a.createdAt < sevenDays);
    const bIsRecentFullPlus = isBFullPlus && (now - b.createdAt < sevenDays);

    if (aIsRecentFullPlus && !bIsRecentFullPlus) return -1;
    if (!aIsRecentFullPlus && bIsRecentFullPlus) return 1;

    // Default to chronological sorting
    return b.createdAt - a.createdAt;
  });

  const getMatchOrder = (m: Match) => {
    if (m.roundNumber !== undefined) return m.roundNumber;
    if (m.round) {
      const matchNum = m.round.match(/\d+/);
      if (matchNum) return parseInt(matchNum[0]);
    }
    return m.createdAt || 0;
  };

  const [publicMatches, setPublicMatches] = useState<Match[]>([]);

  useEffect(() => {
    if (!userProfile?.uid) return;
    const q = query(
      collection(db, getCollectionPath('public_matches')),
      or(
        where('players', 'array-contains', userProfile.uid),
        where('creatorId', '==', userProfile.uid)
      )
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMatches = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];
      setPublicMatches(fetchedMatches);
    });
    return () => unsubscribe();
  }, [userProfile?.uid]);

  const myActiveMatches = matches.filter(m => {
    if (m.tournamentId) {
      const tourney = tournaments.find(t => t.id === m.tournamentId);
      if (!tourney || tourney.isArchived || tourney.status === 'Finalizado') return false;
    }
    return m.status === 'pending' ||
      m.status === 'playing' ||
      m.status === 'disputed' ||
      m.status === 'impugned' ||
      (m.status as any) === 'pending_validation' ||
      (m.status as any) === 'pending_opponent_validation';
  }).sort((a, b) => {
    if (a.tournamentId && b.tournamentId) {
      const orderDiff = getMatchOrder(a) - getMatchOrder(b);
      if (orderDiff !== 0) return orderDiff;
    }
    return (a.createdAt || 0) - (b.createdAt || 0);
  });
  const myActivePublicMatches = publicMatches.filter(m => {
    if (m.date && m.time && isMatchPast(m.date, m.time)) return false;
    return m.status === 'open' || m.status === 'pending' || m.status === 'playing' || m.status === 'disputed' || m.status === 'impugned' || (m.status as any) === 'pending_validation' || (m.status as any) === 'pending_opponent_validation';
  }).sort((a, b) => {
    if (a.date !== b.date) return (a.date || '').localeCompare(b.date || '');
    return (a.time || '').localeCompare(b.time || '');
  });

  const nextTournamentMatch = myActiveMatches[0];
  const nextPublicMatch = myActivePublicMatches[0];



  const formatShortName = (fullName: string) => {
    if (!fullName) return 'Jugador';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0].charAt(0).toUpperCase()}. ${parts[parts.length - 1]}`;
  };

  const renderMatchCard = (matchToRender: Match | undefined, isPublic: boolean) => {
    if (!matchToRender) return null;

    const isTournamentMatch = !!matchToRender?.tournamentId;
    let matchSubtitle = isPublic ? 'Partido Público' : 'Partido Amistoso';
    let matchTitle = isPublic ? 'Tu Próximo Amistoso' : 'Tu Próximo Desafío';
    let zoneAndRoundTag = '';
    let myP1Id, myP2Id, myP1Img, myP2Img, myP1Name, myP2Name;
    let oppP1Id, oppP2Id, oppP1Img, oppP2Img, oppP1Name, oppP2Name;

    if (isTournamentMatch) {
      const tourney = tournaments.find(t => t.id === matchToRender.tournamentId);
      matchSubtitle = tourney ? tourney.name : 'Partido de Torneo';
      zoneAndRoundTag = `${matchToRender.zoneName ? matchToRender.zoneName + ' - ' : ''}${matchToRender.round || 'Llave'}`;

      const team1 = teams.find(t => t.id === matchToRender.team1Id);
      const team2 = teams.find(t => t.id === matchToRender.team2Id);

      const isMyTeam1 = team1?.player1Id === userProfile?.uid || team1?.player2Id === userProfile?.uid || team1?.player3Id === userProfile?.uid;
      const myTeam = isMyTeam1 ? team1 : team2;
      const oppTeam = isMyTeam1 ? team2 : team1;

      myP1Id = myTeam?.player1Id; myP2Id = myTeam?.player2Id;
      oppP1Id = oppTeam?.player1Id; oppP2Id = oppTeam?.player2Id;
    } else if ((matchToRender as any).players) {
      const matchPlayers = (matchToRender as any).players;
      const myIdx = userProfile?.uid ? matchPlayers.indexOf(userProfile.uid) : -1;
      const isMyTeamLeft = myIdx === 0 || myIdx === 1;

      myP1Id = matchPlayers[isMyTeamLeft ? 0 : 2];
      myP2Id = matchPlayers[isMyTeamLeft ? 1 : 3];
      oppP1Id = matchPlayers[isMyTeamLeft ? 2 : 0];
      oppP2Id = matchPlayers[isMyTeamLeft ? 3 : 1];
    }

    if (myP1Id) {
      const p = players.find(x => x.id === myP1Id);
      myP1Img = p?.photoUrl; myP1Name = formatShortName(p?.name || '');
    }
    if (myP2Id) {
      const p = players.find(x => x.id === myP2Id);
      myP2Img = p?.photoUrl; myP2Name = formatShortName(p?.name || '');
    }
    if (oppP1Id) {
      const p = players.find(x => x.id === oppP1Id);
      oppP1Img = p?.photoUrl; oppP1Name = formatShortName(p?.name || '');
    }
    if (oppP2Id) {
      const p = players.find(x => x.id === oppP2Id);
      oppP2Img = p?.photoUrl; oppP2Name = formatShortName(p?.name || '');
    }

    let matchDateText = matchToRender?.date || 'Pendiente';
    let matchTimeText = matchToRender?.time ? `${matchToRender.time} hs` : 'A definir';

    if (isTournamentMatch) {
      const tourney = tournaments.find(t => t.id === matchToRender.tournamentId);
      if (tourney) {
        const { startDate, endDate } = getMatchTemporalState(matchToRender, tourney);
        if (startDate && endDate) {
          const f1 = startDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
          const f2 = endDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
          matchDateText = `Desde el ${f1} al ${f2}`;
          matchTimeText = '';
        }
      }
    }

    return (
      <div className="w-full max-w-[600px] mx-auto mb-4 px-4 sm:px-6 animate-in fade-in slide-in-from-top duration-500">
        <h3 className="text-xs font-black text-white/60 uppercase tracking-wider mb-3 text-center">
          Próxima Fecha de Torneo
        </h3>
        <div
          onClick={() => onMatchClick && onMatchClick(matchToRender)}
          className="w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl border border-white/20 hover:border-white/30 hover:from-white/15 hover:to-white/10 transition-all shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] cursor-pointer group overflow-hidden"
        >
          <div className="flex items-center justify-between bg-black/20 px-2 sm:px-4 py-2 border-b border-white/5 gap-1.5 sm:gap-2">
            {zoneAndRoundTag ? (
              <span className="text-[9px] sm:text-[10px] font-black text-white uppercase tracking-wider shrink-0 bg-white/10 px-1.5 py-0.5 rounded border border-white/5">
                {zoneAndRoundTag}
              </span>
            ) : (
              <span className="w-4 shrink-0"></span>
            )}
            <div className="flex-1 min-w-0 flex justify-center px-1">
              <span className="text-[8px] sm:text-[10px] text-white/70 font-bold uppercase tracking-wider truncate bg-white/5 px-2 py-0.5 rounded-full border border-white/5 max-w-full text-center">
                {matchSubtitle}
              </span>
            </div>
            <span className="text-[9px] sm:text-[10px] font-black text-white/90 whitespace-nowrap shrink-0 bg-white/10 px-1.5 py-0.5 rounded border border-white/5">
              {matchDateText} {matchTimeText ? `- ${matchTimeText}` : ''}
            </span>
          </div>
          <div className="p-3 sm:p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between mt-1 px-1">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-start">
                <div className="flex flex-col items-end text-right flex-1 min-w-0">
                  <span className="text-[11px] sm:text-[13px] font-black text-white/95 leading-tight truncate w-full">{myP1Name}</span>
                  {myP2Id && <span className="text-[11px] sm:text-[13px] font-black text-white/95 leading-tight truncate w-full">{myP2Name}</span>}
                </div>
                <PairAvatar p1Id={myP1Id} p2Id={myP2Id} p1Image={myP1Img} p2Image={myP2Img} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 shadow-lg" onPlayerClick={onPlayerClick} />
              </div>
              <div className="flex flex-col items-center justify-center px-3 sm:px-4 shrink-0">
                <span className="text-[13px] sm:text-[15px] font-black uppercase tracking-widest animate-pulse" style={{ color: config.primaryColor || '#EAB308', textShadow: `0 0 10px ${config.primaryColor || '#EAB308'}` }}>VS</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-end">
                <PairAvatar p1Id={oppP1Id} p2Id={oppP2Id} p1Image={oppP1Img} p2Image={oppP2Img} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 shadow-lg" onPlayerClick={onPlayerClick} />
                <div className="flex flex-col items-start text-left flex-1 min-w-0">
                  <span className="text-[11px] sm:text-[13px] font-black text-white/95 leading-tight truncate w-full">{oppP1Name}</span>
                  {oppP2Id && <span className="text-[11px] sm:text-[13px] font-black text-white/95 leading-tight truncate w-full">{oppP2Name}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col gap-6 max-w-3xl mx-auto pb-24 relative">

      {/* Dashboard Personalizado */}
      {userProfile?.role !== 'admin' && userProfile?.role !== 'superadmin' && (
        <div className="w-full flex flex-col items-center gap-4 animate-in fade-in slide-in-from-top duration-500">

          {/* TWO COLUMNS: Player Card & Widgets */}
          <div className="w-full px-1 sm:px-4 flex justify-center mt-2 mb-4 h-[270px]">
            <div className="flex flex-row items-stretch justify-center gap-1 min-[380px]:gap-3 sm:gap-6 w-full max-w-[520px]">

              {/* Left Column: Player Card with Flip */}
              <div className="w-[204px] shrink-0 h-full relative" style={{ perspective: '1000px' }}>
                <div
                  className="w-[320px] absolute top-0 left-1/2 -translate-x-1/2 origin-top cursor-pointer transition-all duration-700 ease-out"
                  style={{ transformStyle: 'preserve-3d', transform: `scale(0.63) rotateY(${cardRotation}deg)` }}
                  onClick={() => setCardRotation(prev => prev + 180)}
                >
                  <div className="w-full relative" style={{ backfaceVisibility: 'hidden' }}>
                    {(() => {
                      let pToRender = currentPlayer || (userProfile as unknown as Player) || { id: 'guest', name: 'Invitado', role: 'guest', isGuest: true };
                      const pId = pToRender.id || (userProfile as any)?.uid || 'guest';

                      // Compute best rank across all circuits
                      let bestRank = 0;
                      let bestCircuitName = 'Global';
                      let bestIsGlobal = true;
                      let bestCircuitLogo = '';

                      // 1. Calculate Global Rank
                      const sortedGlobal = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.global ?? (b.points || 0)) - (a.rankingPoints?.global ?? (a.points || 0)));
                      const globalRank = sortedGlobal.findIndex(p => p.id === pId) + 1;
                      const globalPoints = pToRender.rankingPoints?.global ?? (pToRender.points || 0);

                      if (globalPoints > 0) {
                        bestRank = globalRank;
                      }

                      // 2. Check Club Ranks
                      if (pToRender.rankingPoints?.clubs) {
                        Object.entries(pToRender.rankingPoints.clubs).forEach(([clubId, points]) => {
                          if (points > 0) {
                            const sortedClub = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.clubs?.[clubId] || 0) - (a.rankingPoints?.clubs?.[clubId] || 0));
                            const clubRank = sortedClub.findIndex(p => p.id === pId) + 1;
                            if (clubRank > 0 && (bestRank === 0 || clubRank < bestRank)) {
                              bestRank = clubRank;
                              const club = sedesPublicas.find(s => s.uid === clubId);
                              bestCircuitName = club?.name || 'Sede Local';
                              bestIsGlobal = false;
                              bestCircuitLogo = club?.photoUrl || '';
                            }
                          }
                        });
                      }

                      const isUnranked = bestRank === 0;

                      return (
                        <PlayerCardPremium
                          player={pToRender}
                          config={config}
                          playerMatches={[]}
                          matchesCount={(pToRender as any)?.stats?.totalCompletedMatches || 0}
                          wins={(pToRender as any)?.stats?.cumulativeWins || 0}
                          losses={0}
                          winRate={0}
                          categoryRank={bestRank}
                          isEnRacha={false}
                          circuitName={bestIsGlobal ? '' : bestCircuitName}
                          circuitLogo={bestIsGlobal ? '' : bestCircuitLogo}
                          isUnranked={isUnranked}
                        />
                      );
                    })()}
                  </div>

                  <div
                    className="absolute inset-0 backface-hidden rounded-[24px] overflow-hidden"
                    style={{
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      backgroundImage: 'url(/card.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                  </div>
                </div>
              </div>

              {/* Right Column: Widgets */}
              <div className="@container flex-1 min-w-[110px] max-w-[280px] flex flex-col gap-2 h-full overflow-y-auto pr-1 no-scrollbar pb-2">

                {/* Próximos Turnos Widget */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/10 flex flex-col shadow-lg backdrop-blur-sm shrink-0 overflow-hidden">
                  <div className="border-b border-white/10 bg-white/[0.02] py-2 px-1 sm:px-3 shrink-0">
                    <h4 className="text-[length:clamp(6.5px,6.5cqw,12px)] font-black text-white/70 uppercase tracking-tighter min-[380px]:tracking-widest flex items-center justify-center gap-1 text-center w-full m-0 whitespace-nowrap">
                      <MapPin size={10} style={{ color: config.primaryColor || '#EAB308' }} className="shrink-0" /> Próximos Turnos
                    </h4>
                  </div>
                  <div className="p-3">
                    {myBookings.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {myBookings.slice(0, 2).map((b, i) => {
                          let dateStr = '';
                          if (b.date) {
                            const today = new Date();
                            const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
                            const todayStr = today.toISOString().split('T')[0];
                            const tomorrowStr = tomorrow.toISOString().split('T')[0];

                            const dateObj = new Date(b.date + 'T12:00:00');
                            const dayNames = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
                            const dayName = dayNames[dateObj.getDay()];
                            const [yyyy, mm, dd] = b.date.split('-');

                            if (b.date === todayStr) dateStr = 'Hoy';
                            else if (b.date === tomorrowStr) dateStr = 'Mañana';
                            else {
                              dateStr = `${dayName} ${dd}/${mm}`;
                            }
                          }
                          return (
                            <div key={i} className="bg-black/20 rounded-lg p-2 border border-white/5 flex items-center justify-between gap-2">
                              <div className="flex flex-col min-w-0">
                                <p className="text-white font-black text-[11px] sm:text-xs truncate uppercase">{dateStr}</p>
                                <p className="text-white/40 text-[9px] font-medium">{b.courtName || 'Cancha'}</p>
                              </div>
                              <div className="shrink-0 bg-white/10 px-2 py-1 rounded text-[10px] sm:text-[11px] font-bold" style={{ color: config.primaryColor || '#EAB308' }}>
                                {b.timeSlot || b.startTime || '?'}hs
                              </div>
                            </div>
                          );
                        })}
                        {myBookings.length > 2 && (
                          <p className="text-center text-[10px] text-white/40 mt-1">+{myBookings.length - 2} más</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-2 text-center gap-2">
                        <p className="text-[10px] sm:text-[11px] text-white/50 font-medium leading-tight">No tienes turnos próximos</p>
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('changeTab', { detail: { tab: 'reservas' } }));
                            setTimeout(() => window.dispatchEvent(new CustomEvent('changeBookingSubTab', { detail: { tab: 'grilla' } })), 100);
                          }}
                          className="px-1 py-1.5 rounded-lg text-black font-black uppercase text-[length:clamp(6.5px,6.5cqw,10px)] tracking-tighter min-[380px]:tracking-normal w-full shadow-[0_0_15px_rgba(250,204,21,0.3)] transition-all hover:scale-105 whitespace-nowrap"
                          style={{ backgroundColor: config.primaryColor || '#EAB308' }}
                        >
                          Reservar Cancha
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actividad Reciente Widget */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/10 flex flex-col shadow-lg backdrop-blur-sm shrink-0 overflow-hidden">
                  <div className="border-b border-white/10 bg-white/[0.02] py-2 px-1 sm:px-3 shrink-0">
                    <h4 className="text-[length:clamp(6.5px,6cqw,10px)] font-black text-white/70 uppercase tracking-tighter min-[380px]:tracking-widest flex items-center justify-center gap-1 text-center w-full m-0 whitespace-nowrap">
                      <History size={10} style={{ color: config.primaryColor || '#EAB308' }} className="shrink-0" /> Actividad Reciente
                    </h4>
                  </div>
                  <div className="p-2.5">
                    <div className="flex flex-col gap-1.5">
                      {recentNotifications && recentNotifications.length > 0 ? (
                        recentNotifications.slice(0, 2).map((n, i) => {
                          let icon = <Bell size={6} />;
                          let color = 'text-white/40';
                          const titleLower = n.title?.toLowerCase() || '';
                          const bodyLower = n.body?.toLowerCase() || '';

                          if (n.type === 'player_validation') {
                            icon = <BadgeCheck size={6} />;
                            color = 'text-emerald-400';
                          } else if (n.points) {
                            icon = <Trophy size={6} />;
                            color = 'text-amber-400';
                          } else if (n.type === 'tournament_registration') {
                            icon = <Users size={6} />;
                            color = 'text-blue-400';
                          } else if (n.type === 'tournament_started') {
                            icon = <Trophy size={6} />;
                            color = 'theme-primary-text';
                          } else if (titleLower.includes('cancelado') || bodyLower.includes('cancelado')) {
                            icon = <CalendarX size={6} />;
                            color = 'text-red-400';
                          } else if (titleLower.includes('turno') || titleLower.includes('reserva') || bodyLower.includes('turno') || bodyLower.includes('reserva')) {
                            icon = <CalendarCheck size={6} />;
                            color = 'text-emerald-400';
                          } else if (titleLower.includes('invitación') || bodyLower.includes('invitación')) {
                            icon = <Mail size={6} />;
                            color = 'text-blue-400';
                          }

                          return (
                            <div
                              key={i}
                              onClick={() => setSelectedNotification(n)}
                              className="flex items-center gap-2 group/notif cursor-pointer hover:bg-white/5 p-1 rounded-lg transition-colors"
                            >
                              <div className={`w-4 h-4 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0 ${color}`}>
                                {icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white/90 font-bold text-[8.5px] truncate leading-tight">{n.title}</p>
                                <p className="text-white/40 text-[7.5px] leading-tight line-clamp-2">{n.body}</p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[8px] text-white/30 text-center py-2 italic font-medium">No hay actividad reciente.</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* SECCION: TUS PROXIMOS DESAFIOS */}
          {nextTournamentMatch && renderMatchCard(nextTournamentMatch, false)}


          {!nextTournamentMatch && (
            <div className="w-full max-w-[600px] mx-auto mb-4 px-4 sm:px-6">
              <h3 className="text-sm font-black text-white uppercase tracking-wider mb-3 flex items-center justify-center gap-2">
                <Zap size={16} style={{ color: config.primaryColor || '#EAB308' }} /> Tu Próximo Desafío
              </h3>
              <div className="flex flex-col items-center justify-center bg-white/5 rounded-xl py-6 px-6 border border-white/5 text-center gap-3">
                <p className="text-[10px] text-white/50 font-bold uppercase tracking-wider">Aún no tienes partidos de torneos próximos</p>
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('changeTab', { detail: { tab: 'torneos' } }));
                  }}
                  className="mt-1 px-8 py-2 rounded-lg text-black font-black uppercase text-xs shadow-[0_0_10px_rgba(250,204,21,0.2)] hover:scale-105 transition-all"
                  style={{ backgroundColor: config.primaryColor || '#EAB308' }}
                >
                  Ver Torneos
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Flash Banners Carousel */}
      {activeFlashBanners.length > 0 && activeFlashBanners[currentSlide] && (
        <div className="w-full relative group animate-in fade-in slide-in-from-top duration-300">
          <div className="w-full overflow-hidden rounded-2xl bg-gradient-to-r from-purple-950/60 via-slate-900/80 to-purple-950/60 border border-purple-500/20 hover:border-purple-500/40 py-2 px-3 sm:py-2.5 sm:px-4 backdrop-blur-md shadow-[0_4px_16px_rgba(168,85,247,0.1)] flex items-center justify-between gap-2 relative">

            {/* Micro pulsating light on the left edge */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 animate-pulse" />

            {/* Slide Container with fixed height to prevent resizing layout shifts */}
            <div className="flex-1 min-w-0 h-10 relative overflow-hidden">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={currentSlide}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 220, damping: 24 },
                    opacity: { duration: 0.25 }
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={1}
                  onDragEnd={(e, { offset, velocity }) => {
                    const swipe = Math.abs(offset.x) * velocity.x;
                    if (swipe < -10000) {
                      paginate(1);
                    } else if (swipe > 10000) {
                      paginate(-1);
                    }
                  }}
                  onClick={() => {
                    const banner = activeFlashBanners[currentSlide];
                    if (banner.clubId === 'agunavarro1989@gmail.com' || banner.clubName.toLowerCase().includes('admin')) {
                      onSupportClick?.();
                    } else {
                      onContactClick?.(banner.clubId, banner.clubName, banner.clubPhotoUrl);
                    }
                  }}
                  className="absolute inset-0 flex items-center gap-2 min-w-0 cursor-pointer px-0.5"
                >
                  {/* Club Logo */}
                  <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 border border-white/10 shadow-md">
                    {activeFlashBanners[currentSlide].clubPhotoUrl ? (
                      <FadeInImage src={activeFlashBanners[currentSlide].clubPhotoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300">
                        {activeFlashBanners[currentSlide].clubName.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Text Contents */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-bold text-white/50 truncate">
                        {activeFlashBanners[currentSlide].clubName}
                      </span>
                      <Zap size={8} className="fill-amber-400 text-amber-400 animate-pulse shrink-0" />
                    </div>
                    <p className="text-[11px] sm:text-xs text-white/95 font-semibold tracking-wide leading-tight group-hover:text-white transition-colors line-clamp-2 break-words">
                      {activeFlashBanners[currentSlide].content}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Chevron nav overlays on slide container edges */}
            {activeFlashBanners.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); paginate(-1); }}
                  className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 border border-white/10 flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-all z-20"
                >
                  <ChevronLeft size={12} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); paginate(1); }}
                  className="absolute right-10 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 border border-white/10 flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-all z-20"
                >
                  <ChevronRight size={12} />
                </button>
              </>
            )}

            {/* Message icon — always visible, no dead space */}
            <div
              onClick={() => {
                const banner = activeFlashBanners[currentSlide];
                if (banner.clubId === 'agunavarro1989@gmail.com' || banner.clubName.toLowerCase().includes('admin')) {
                  onSupportClick?.();
                } else {
                  onContactClick?.(banner.clubId, banner.clubName, banner.clubPhotoUrl);
                }
              }}
              className="w-8 h-8 shrink-0 rounded-xl bg-purple-500/10 group-hover:bg-purple-500/20 border border-purple-500/20 flex items-center justify-center cursor-pointer transition-all hover:scale-105 z-10"
            >
              <MessageSquare size={14} className="text-purple-300 group-hover:text-white" />
            </div>


          </div>

          {/* Dots Indicator (Only if multiple banners) */}
          {activeFlashBanners.length > 1 && (
            <div className="flex gap-1 justify-center mt-1.5">
              {activeFlashBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const diff = idx - currentSlide;
                    if (diff !== 0) {
                      paginate(diff > 0 ? 1 : -1);
                    }
                  }}
                  className={`h-1 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-purple-500 w-3' : 'bg-white/20 w-1'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header and Tabs */}
      {/* Header and City Selector */}
      <div className="flex flex-row items-center justify-center gap-4 w-full">
        <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/20"></div>
        <h2 className="text-[28px] sm:text-[34px] md:text-[44px] font-black uppercase italic tracking-tighter text-white whitespace-nowrap shrink-0 text-center">
          Comunidad <span style={{ color: primary }}>RPT</span>
        </h2>
        <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/20"></div>
      </div>

      {/* Filter Chips */}
      <div className="w-full">
        <FilterChips
          categories={categories}
          selectedCategories={selectedCategories}
          onToggleCategory={(cat) => setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
          onClearCategories={() => setSelectedCategories([])}
          genders={genders}
          selectedGender={selectedGender}
          onSelectGender={setSelectedGender}
          primaryColor={primary}
        />
      </div>

      {/* Post Creator */}
      {userProfile && (
        <PostCreator
          userProfile={userProfile}
          primaryColor={primary}
          currentPlayer={currentPlayer}
          clubProfile={userProfile && userProfile.role !== 'user' ? (userProfile.role === 'superadmin' ? userProfile : (sedesPublicas.find(s => s.uid === userProfile.clubId || (userProfile.role === 'admin' && s.uid === userProfile.uid)))) : undefined}
          selectedCity={globalCity}
        />
      )}

      {/* Feed List */}
      <div className="flex flex-col gap-6">
        {sortedPosts.map(post => {
          const isFullPlus = post.isPriority || (post.authorRole === 'admin' && sedesPublicas.find(s => s.uid === post.authorId)?.subscriptionPlan === 'Full Plus');
          return (
            <PostCard
              key={post.id}
              post={post}
              primaryColor={primary}
              userProfile={userProfile}
              isFullPlus={isFullPlus}
              onContactClick={onContactClick}
              onPlayerClick={onPlayerClick}
              onSedeClick={onSedeClick}
              config={config}
              selectedCity={globalCity}
            />
          );
        })}
        {sortedPosts.length === 0 && (
          <div className="text-center py-10 text-white/40 text-sm font-medium">
            No hay publicaciones recientes en esta sección.
          </div>
        )}

        {/* Invisible div for Intersection Observer */}
        {sortedPosts.length > 0 && sortedPosts.length >= postLimit && (
          <div ref={bottomBoundaryRef} className="h-10 w-full flex items-center justify-center">
            <span className="text-white/30 text-xs font-semibold animate-pulse">Cargando más publicaciones...</span>
          </div>
        )}
      </div>
      {/* Modal Detalles de Notificación */}
      <AnimatePresence>
        {selectedNotification && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedNotification(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative w-full max-w-sm bg-[#222530] border border-[#EAB308]/20 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-5 flex flex-col gap-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#EAB308]/10 flex items-center justify-center border border-[#EAB308]/20 shrink-0 shadow-sm">
                      {(() => {
                        const titleLower = selectedNotification.title?.toLowerCase() || '';
                        const bodyLower = selectedNotification.body?.toLowerCase() || '';

                        if (selectedNotification.type === 'player_validation') return <BadgeCheck className="text-emerald-400" size={20} />;
                        if (selectedNotification.points || selectedNotification.type === 'tournament_started') return <Trophy className={selectedNotification.points ? 'text-amber-400' : 'theme-primary-text'} size={20} />;
                        if (selectedNotification.type === 'tournament_registration') return <Users className="text-blue-400" size={20} />;
                        if (titleLower.includes('cancelado') || bodyLower.includes('cancelado')) return <CalendarX className="text-red-400" size={20} />;
                        if (titleLower.includes('turno') || titleLower.includes('reserva') || bodyLower.includes('turno') || bodyLower.includes('reserva')) return <CalendarCheck className="text-emerald-400" size={20} />;
                        if (titleLower.includes('invitación') || bodyLower.includes('invitación')) return <Mail className="text-blue-400" size={20} />;
                        return <Bell className="text-[#EAB308]" size={20} />;
                      })()}
                    </div>
                    <div>
                      <h3 className="font-black text-white text-sm uppercase tracking-wide leading-tight">{selectedNotification.title}</h3>
                      <p className="text-[10px] text-[#EAB308]/80 mt-1 uppercase font-bold tracking-widest">
                        {new Date(selectedNotification.createdAt || Date.now()).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNotification(null)}
                    className="p-1.5 rounded-full hover:bg-white/5 transition-colors shrink-0 text-white/50 hover:text-white"
                  >
                    <ChevronLeft size={16} className="rotate-180" />
                  </button>
                </div>

                <div className="bg-black/30 rounded-xl p-4 border border-white/5 shadow-inner">
                  <p className="text-sm text-white/90 leading-relaxed font-medium">
                    {selectedNotification.body}
                  </p>
                </div>

                <button
                  onClick={() => setSelectedNotification(null)}
                  className="w-full py-3 hover:brightness-110 text-black font-black uppercase text-xs tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(250,204,21,0.2)] mt-2"
                  style={{ backgroundColor: config.primaryColor || '#EAB308' }}
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
